// ---------------------------------------------------------------------------------------------
// Integrations — the shared framework every Phase 3 provider sits behind.
//
// Nothing here carries a credential. Tokens, webhook secrets and provider passwords live
// encrypted in the database and never appear in an API response; the client only ever learns
// whether a connection is configured and how its last sync went.
// ---------------------------------------------------------------------------------------------
export const INTEGRATION_PROVIDER = {
    GITHUB: 'GITHUB',
    GITLAB: 'GITLAB',
    EMAIL: 'EMAIL',
    WHATSAPP: 'WHATSAPP',
    AI: 'AI',
    /**
     * Ashniva IVR.
     *
     * An IVR account is an integration connection like any other: encrypted credentials, an enable
     * switch, an external account id that attributes an unauthenticated delivery to a tenant, and
     * the idempotency index on integration events. A parallel table for telephony would be a second
     * copy of all four to keep right.
     */
    IVR: 'IVR',
    /**
     * Ashniva Theme Manager.
     *
     * A separate product that publishes design tokens. It is a connection like any other for the
     * same reasons the IVR one is: encrypted credentials, an enable switch, a base URL in
     * non-secret settings, and a webhook secret already sitting there for the day the direction is
     * decided to be push rather than pull. See `docs/theme-manager-integration.md` — the vendor
     * half of that contract does not exist yet, and nothing here guesses at it.
     */
    THEME_MANAGER: 'THEME_MANAGER',
};
export const INTEGRATION_PROVIDER_LABELS = {
    GITHUB: 'GitHub',
    GITLAB: 'GitLab',
    EMAIL: 'Email',
    WHATSAPP: 'WhatsApp',
    AI: 'AI provider',
    IVR: 'IVR and support calls',
    THEME_MANAGER: 'Theme Manager',
};
export const INTEGRATION_STATUS = {
    /** No credentials stored yet. */
    DISCONNECTED: 'DISCONNECTED',
    /** Credentials stored and last validated successfully. */
    CONNECTED: 'CONNECTED',
    /** Stored credentials were rejected, or the last sync failed permanently. */
    ERROR: 'ERROR',
    /** The stored token passed its expiry and could not be refreshed. */
    EXPIRED: 'EXPIRED',
};
export const INTEGRATION_STATUS_LABELS = {
    DISCONNECTED: 'Not connected',
    CONNECTED: 'Connected',
    ERROR: 'Needs attention',
    EXPIRED: 'Sign-in expired',
};
/** How the last synchronisation ended. */
export const SYNC_STATUS = {
    NEVER: 'NEVER',
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
};
// ---------------------------------------------------------------------------------------------
// Webhook events
// ---------------------------------------------------------------------------------------------
export const INTEGRATION_EVENT_STATUS = {
    RECEIVED: 'RECEIVED',
    PROCESSED: 'PROCESSED',
    FAILED: 'FAILED',
    /** Seen before — the unique (provider, externalEventId) constraint caught it. */
    DUPLICATE: 'DUPLICATE',
    /** Signature verification failed; nothing was processed. */
    REJECTED: 'REJECTED',
};
// ---------------------------------------------------------------------------------------------
// Git integration
//
// Development activity is internal. None of these types has a client-portal counterpart, and no
// portal endpoint returns them: commit messages, branch names, PR titles and reviewer names stay
// inside the provider organization.
// ---------------------------------------------------------------------------------------------
export const CODE_ACTIVITY_KIND = {
    COMMIT: 'COMMIT',
    BRANCH_PUSH: 'BRANCH_PUSH',
    PULL_REQUEST: 'PULL_REQUEST',
    REVIEW: 'REVIEW',
    MERGE: 'MERGE',
    RELEASE: 'RELEASE',
    TAG: 'TAG',
};
export const CODE_ACTIVITY_KIND_LABELS = {
    COMMIT: 'Commit',
    BRANCH_PUSH: 'Branch push',
    PULL_REQUEST: 'Pull request',
    REVIEW: 'Review',
    MERGE: 'Merged',
    RELEASE: 'Release',
    TAG: 'Tag',
};
// ---------------------------------------------------------------------------------------------
// Release notes
//
// The one Phase 3 artefact that crosses the internal/client boundary. `internalNotes` and the
// approval trail have no counterpart in the portal types below, by construction.
// ---------------------------------------------------------------------------------------------
export const RELEASE_NOTE_STATUS = {
    DRAFT: 'DRAFT',
    IN_REVIEW: 'IN_REVIEW',
    CHANGES_REQUESTED: 'CHANGES_REQUESTED',
    APPROVED: 'APPROVED',
    PUBLISHED: 'PUBLISHED',
    CANCELLED: 'CANCELLED',
};
export const RELEASE_NOTE_STATUS_LABELS = {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    CHANGES_REQUESTED: 'Changes requested',
    APPROVED: 'Approved',
    PUBLISHED: 'Published',
    CANCELLED: 'Cancelled',
};
/** Only a published note is ever readable in the client portal. */
export const CLIENT_VISIBLE_RELEASE_NOTE_STATUSES = ['PUBLISHED'];
export const RELEASE_NOTE_ITEM_KIND = {
    TASK: 'TASK',
    TICKET: 'TICKET',
    CLIENT_UPDATE: 'CLIENT_UPDATE',
    CODE_ACTIVITY: 'CODE_ACTIVITY',
    MANUAL: 'MANUAL',
};
export const RELEASE_NOTE_ITEM_KIND_LABELS = {
    TASK: 'Task',
    TICKET: 'Ticket',
    CLIENT_UPDATE: 'Client update',
    CODE_ACTIVITY: 'Development',
    MANUAL: 'Added manually',
};
/**
 * The transitions the API allows. Anything not listed is refused by the service, so a client of
 * the API cannot move a note sideways into a state the workflow does not have an edge to.
 */
export const RELEASE_NOTE_TRANSITIONS = {
    DRAFT: ['IN_REVIEW', 'CANCELLED'],
    IN_REVIEW: ['APPROVED', 'CHANGES_REQUESTED', 'CANCELLED'],
    CHANGES_REQUESTED: ['DRAFT', 'IN_REVIEW', 'CANCELLED'],
    APPROVED: ['PUBLISHED', 'DRAFT', 'CANCELLED'],
    PUBLISHED: [],
    CANCELLED: ['DRAFT'],
};
export function canTransitionReleaseNote(from, to) {
    return RELEASE_NOTE_TRANSITIONS[from].includes(to);
}
//# sourceMappingURL=integrations.js.map